import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Gift, ChevronDown, ChevronUp, DollarSign, Clock, Heart, StickyNote, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import type { OfferPrefill } from './OfferBuilder';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

interface SentOffer {
  id: string;
  user_id: string;
  /** The role this shop authored on the offer. See OfferBuilder. */
  offered_role: string | null;
  pay_type: string | null;
  pay_rate: number | null;
  bonus_criteria: string | null;
  working_hours: any;
  health_insurance: boolean;
  dental_insurance: boolean;
  vision_insurance: boolean;
  life_insurance: boolean;
  retirement_401k: boolean;
  retirement_match: string | null;
  pto_days: number | null;
  uniforms_provided: boolean;
  tools_provided: boolean;
  additional_benefits: string | null;
  notes: string | null;
  status: string;
  decline_reason: string | null;
  created_at: string;
}

/** Presentation only — mirrors OFFERABLE_ROLES in OfferBuilder. */
const ROLE_LABELS: Record<string, string> = {
  TECHNICIAN: 'Technician',
  SERVICE_WRITER: 'Service Writer',
  MANAGER: 'Manager',
  DISPATCHER: 'Dispatcher',
  PARTS: 'Parts',
  AUDITOR: 'Auditor',
};

const DAY_LABELS: Record<string, string> = {
  Sunday: 'Sun', Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed',
  Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat',
};

function to12Hour(time: string): string {
  if (!time) return time;
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${ampm}`;
}

function useUserNames(userIds: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (userIds.length === 0) return;
    supabase
      .from('profiles')
      .select('id, display_name, email')
      .in('id', [...new Set(userIds)])
      .then(({ data }) => {
        if (data) {
          const map: Record<string, string> = {};
          data.forEach((p: any) => { map[p.id] = p.display_name || p.email || 'User'; });
          setNames(map);
        }
      });
  }, [userIds.join(',')]);
  return names;
}

export function OffersSent({ onCounterOffer }: { onCounterOffer?: (prefill: OfferPrefill) => void }) {
  const { activeShop } = useAuth();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: offers, isLoading } = useQuery({
    queryKey: ['connect-offers-sent', activeShop?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('connect_offers')
        .select('*')
        .eq('shop_id', activeShop!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as SentOffer[];
    },
    enabled: !!activeShop?.id,
  });

  const userIds = (offers || []).map(o => o.user_id);
  const userNames = useUserNames(userIds);

  if (isLoading) return null;
  if (!offers?.length) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Sent Offers</CardTitle>
          </div>
          <CardDescription>No offers sent yet. Use the Make an Offer section above to send one.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const statusVariant = (s: string) => {
    if (s === 'accepted') return 'default';
    if (s === 'declined') return 'destructive';
    return 'secondary';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Sent Offers</CardTitle>
          <Badge variant="secondary">{offers.length}</Badge>
        </div>
        <CardDescription>Track offers you've sent to connected users.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {offers.map((offer) => {
          const expanded = expandedId === offer.id;
          const userName = userNames[offer.user_id] || 'User';
          const workingDays = Array.isArray(offer.working_hours)
            ? offer.working_hours.filter((d: any) => d.enabled)
            : [];

          return (
            <div key={offer.id} className="rounded-lg border">
              <button
                className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors text-left"
                onClick={() => setExpandedId(expanded ? null : offer.id)}
              >
                <div>
                  <p className="text-sm font-medium">{userName}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(offer.created_at).toLocaleDateString()}
                    {offer.offered_role && ` · ${ROLE_LABELS[offer.offered_role] || offer.offered_role}`}
                    {offer.pay_type && ` · ${offer.pay_type.replace('_', ' ')}`}
                    {offer.pay_rate != null && ` · $${offer.pay_rate.toLocaleString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusVariant(offer.status)} className="capitalize text-xs">
                    {offer.status}
                  </Badge>
                  {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
              </button>

              {expanded && (
                <div className="border-t px-3 pb-3 pt-2 space-y-4 text-sm">
                  {/* Pay */}
                  <div>
                    <div className="flex items-center gap-1.5 font-medium mb-1">
                      <DollarSign className="h-3.5 w-3.5" /> Pay Structure
                    </div>
                    <div className="text-muted-foreground space-y-0.5 pl-5">
                      {offer.pay_type && <p>Type: <span className="text-foreground capitalize">{offer.pay_type.replace('_', ' ')}</span></p>}
                      {offer.pay_rate != null && <p>Rate: <span className="text-foreground">${offer.pay_rate.toLocaleString()}</span></p>}
                      {offer.bonus_criteria && <p>Bonus: <span className="text-foreground">{offer.bonus_criteria}</span></p>}
                    </div>
                  </div>

                  {/* Hours */}
                  {workingDays.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 font-medium mb-1">
                        <Clock className="h-3.5 w-3.5" /> Working Hours
                      </div>
                      <div className="text-muted-foreground space-y-0.5 pl-5">
                        {workingDays.map((d: any) => (
                          <p key={d.day}>
                            <span className="text-foreground">{DAY_LABELS[d.day] || d.day}:</span> {to12Hour(d.start)} – {to12Hour(d.end)}
                          </p>
                        ))}
                        {(offer as any).lunch_break_start && (offer as any).lunch_break_end && (
                          <p>
                            <span className="text-foreground">Lunch:</span> {to12Hour((offer as any).lunch_break_start)} – {to12Hour((offer as any).lunch_break_end)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  <div>
                    <div className="flex items-center gap-1.5 font-medium mb-1">
                      <Heart className="h-3.5 w-3.5" /> Benefits
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-5">
                      {offer.health_insurance && <Badge variant="outline" className="text-xs">Health Insurance</Badge>}
                      {offer.dental_insurance && <Badge variant="outline" className="text-xs">Dental</Badge>}
                      {offer.vision_insurance && <Badge variant="outline" className="text-xs">Vision</Badge>}
                      {offer.life_insurance && <Badge variant="outline" className="text-xs">Life Insurance</Badge>}
                      {offer.retirement_401k && <Badge variant="outline" className="text-xs">401(k){offer.retirement_match ? ` (${offer.retirement_match})` : ''}</Badge>}
                      {offer.pto_days != null && offer.pto_days > 0 && <Badge variant="outline" className="text-xs">{offer.pto_days} PTO Days</Badge>}
                      {offer.uniforms_provided && <Badge variant="outline" className="text-xs">Uniforms</Badge>}
                      {offer.tools_provided && <Badge variant="outline" className="text-xs">Tools Provided</Badge>}
                      {offer.additional_benefits && <Badge variant="outline" className="text-xs">{offer.additional_benefits}</Badge>}
                    </div>
                  </div>

                  {/* Notes */}
                  {offer.notes && (
                    <div>
                      <div className="flex items-center gap-1.5 font-medium mb-1">
                        <StickyNote className="h-3.5 w-3.5" /> Notes
                      </div>
                      <p className="text-muted-foreground pl-5 whitespace-pre-wrap">{offer.notes}</p>
                    </div>
                  )}

                  {/* Decline reason + counter offer */}
                  {offer.status === 'declined' && (
                    <div className="pt-2 border-t space-y-3">
                      {offer.decline_reason && (
                        <div>
                          <p className="text-sm font-medium text-destructive">Decline Reason:</p>
                          <p className="text-sm text-muted-foreground mt-0.5">{offer.decline_reason}</p>
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          onCounterOffer?.({
                            user_id: offer.user_id,
                            pay_type: offer.pay_type,
                            pay_rate: offer.pay_rate,
                            bonus_criteria: offer.bonus_criteria,
                            working_hours: offer.working_hours,
                            health_insurance: offer.health_insurance,
                            dental_insurance: offer.dental_insurance,
                            vision_insurance: offer.vision_insurance,
                            life_insurance: offer.life_insurance,
                            retirement_401k: offer.retirement_401k,
                            retirement_match: offer.retirement_match,
                            pto_days: offer.pto_days,
                            uniforms_provided: offer.uniforms_provided,
                            tools_provided: offer.tools_provided,
                            additional_benefits: offer.additional_benefits,
                            notes: offer.notes,
                            lunch_break_start: (offer as any).lunch_break_start,
                            lunch_break_end: (offer as any).lunch_break_end,
                          });
                          toast.info('🔄 Counter Offer', { description: 'The offer builder has been pre-filled. Adjust terms and send.' });
                          document.getElementById('offer-builder')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Counter Offer
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
